import { create } from 'zustand';

// Fabric canvas is dynamically imported — use any to avoid static type dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FabricCanvas = any;

// Shared with useFabricCanvas.ts's snapshot listener, so a manually-pushed
// snapshot (see `restoreCanvasJsonSilently`'s callers) serializes the same
// custom properties the automatic one does.
//
// `printColors` (BackgroundColorTool.tsx's commitToCanvas — the resolved hex
// palette an uploaded image was quantized to in the Select Colors step) was
// missing from this list entirely until 2026-08-06 — fabric.Object.toJSON()
// only ever serializes properties explicitly named here (or on Fabric's own
// built-in list), so `img.set({ printColors: [...] })` silently vanished on
// every save/autosave/undo-snapshot/download, regardless of whether the
// object actually carried it in memory at commit time. Reported live: a
// customer picked "2" in Select Colors, the wizard's own on-screen preview
// correctly showed the 2-color quantized image, yet the saved design's
// printColors always came back null/undefined — the Design Guide PDF then
// had nothing to show but "Full color — no color limit applied" for every
// image, regardless of what was actually picked. Every one of the (still
// separately duplicated, not yet consolidated) inline toJSON() property
// lists in useAutoSave.ts/CartPopup.tsx/DesignerHeader.tsx(×2)/
// designDownload.ts needed the identical addition — see IMPLEMENTATION-
// STATUS.md's changelog entry for this fix for the full list.
export const HISTORY_SNAPSHOT_PROPS = [
  'name', 'tab', 'imageSrc', 'converted_orignal_file_url', 'whiteURL', 'originalURL', 'originalUploadId', 'originalUploadUrl', 'printColors', 'fromTemplate', 'spotColorId',
];

// `canvas.loadFromJSON()` clears the canvas first via Fabric's real `remove()`
// (one genuine 'object:removed' per existing object) before repopulating it
// via `insertAt` (which fires nothing) — so *every* programmatic restore
// (undo, redo, applying a template, loading a different saved design) looks
// like a real edit to anything listening for 'object:removed', chiefly
// useFabricCanvas.ts's own history-snapshot listener. Left unguarded, that
// listener captures the canvas's momentarily-*empty* in-between state (the
// gap between `clear()` and the restored objects actually landing) and queues
// it via the debounced `pushSnapshot` — which 500ms later silently overwrites
// `future` with `[]` even though nothing the user did caused it. That's
// exactly what broke "Redo right after Undo": any pause longer than the debounce
// before clicking Redo was enough for it to fire and wipe the stack out from
// under the click. Setting `__suppressHistory` on the canvas instance for the
// duration of the restore tells that listener to ignore it.
export function restoreCanvasJsonSilently(canvas: FabricCanvas, json: any): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      canvas.__suppressHistory = false;
      resolve();
    };

    canvas.__suppressHistory = true;
    canvas.loadFromJSON(json, () => {
      // Fabric's own internal `__setupCanvas` (what eventually runs this
      // callback) already calls its *own* `renderAll()` — synchronously
      // before invoking this callback at all — so by this point the restored
      // objects are already correctly in place regardless of what happens
      // next. This second call only exists to actually repaint.
      try { canvas.renderAll(); } catch { /* see the setTimeout below */ }
      finish();
    });

    // Safety net for a real, observed failure mode: if that *internal*
    // `renderAll()` inside `__setupCanvas` throws (confirmed live — a
    // product whose masking image 404s crashes every `renderAll()` from
    // inside Fabric's own clip-path rendering step), the exception aborts
    // `__setupCanvas` before it ever reaches `callback && callback()` —
    // meaning the callback above never runs at all, `__suppressHistory`
    // stays stuck `true` forever, and every edit for the rest of the session
    // silently stops being recorded into history. The object list itself is
    // already correct by then (`__setupCanvas` re-adds objects via `insertAt`
    // *before* its own `renderAll()` call), so it's safe to force-unstick
    // here on a short timer regardless of whether the real callback ever
    // fires — `finish()`'s `settled` guard makes this a no-op if it already
    // ran normally.
    setTimeout(finish, 300);
  });
}

interface HistoryStore {
  past:    string[];
  future:  string[];
  pushSnapshot: (json: string) => void;
  init:    (json: string) => void;
  undo:    (canvas: FabricCanvas) => void;
  redo:    (canvas: FabricCanvas) => void;
  clear:   () => void;
}

const MAX_HISTORY = 50;

let debounceTimer: ReturnType<typeof setTimeout>;

export const useHistoryStore = create<HistoryStore>((set, get) => ({
  past:   [],
  future: [],

  pushSnapshot: (json) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      set(state => ({
        past:   [...state.past.slice(-MAX_HISTORY + 1), json],
        future: [],
      }));
    }, 500);
  },

  // Seeds the baseline state a side starts from (on mount, or after switching
  // sides) so the first real edit gives undo something to step back to.
  // Without this, `past` only ever grows from edits — `undo()`'s `length < 2`
  // guard then has nothing to fall back to until a *second* edit is made,
  // making the very first Undo click after a single change silently no-op.
  init: (json) => {
    clearTimeout(debounceTimer);
    set({ past: [json], future: [] });
  },

  // Restoring via `loadFromJSON` also never fires 'object:added'/'removed' for
  // the objects it *re-adds* (only the transient clear above, already
  // neutralised by `restoreCanvasJsonSilently`) — so anything that rebuilds
  // its own state off those events (LayerPanel, the sidebar's layer-count
  // badge, ColorDetectionPanel) would otherwise go stale after undo/redo even
  // though the canvas itself repaints correctly. Firing a dedicated
  // 'history:restored' event (rather than 'object:added') lets those
  // listeners refresh without it being mistaken for a real edit.
  undo: (canvas) => {
    const { past, future } = get();
    if (past.length < 2) return;
    const current = past[past.length - 1];
    const prev    = past[past.length - 2];
    set({ past: past.slice(0, -1), future: [current, ...future] });
    restoreCanvasJsonSilently(canvas, prev).then(() => canvas.fire('history:restored'));
  },

  redo: (canvas) => {
    const { past, future } = get();
    if (!future.length) return;
    const [next, ...rest] = future;
    set({ past: [...past, next], future: rest });
    restoreCanvasJsonSilently(canvas, next).then(() => canvas.fire('history:restored'));
  },

  clear: () => set({ past: [], future: [] }),
}));
